import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react'
import { format } from 'date-fns'
import { getAllDocuments } from '../../api'
import { useNavigate } from 'react-router-dom'
import {
  getCategories,
  getDecoratedPointOfWorks,
  getInvoiceGroups,
  getOrganisations,
  getPartners,
  getPointOfWorks,
  getStatuses
} from '../../api/getNomenclatures'
import { getLastSPVCrawls } from '../../api/getLastSPVCrawl'
import { exportPdfRequest } from '../../api/exportDocumentPdf'
import { exportXmlRequest } from '../../api/exportDocumentXml'
import {
  FaChevronLeft,
  FaChevronRight,
  FaFile,
  FaFileExcel,
  FaFilePdf,
  FaFolder,
  FaSearch,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaTrash
} from 'react-icons/fa'
import { exportDocumentZIPRequest } from '../../api/exportDocumentZIPRequest'
import getPrettyStatus from '../../../../helpers/status'
import DocumentStatus from '../../../../constants/documentStatus'
import getWCRShortName from '../../../../helpers/wcrCompany'
import { DOCUMENT_STATUS, LABEL, MESSAGE } from '../../../../constants'
import FilterPopoverWithCheck from './FilterPopoverWithCheck'
import { useAuth } from '../../../../auth'
import { DateRangePicker } from '../../../../components'
import FilterMultiSelectPopover from './FilterMultiSelectPopover'
import { useDebounce } from '../../../../hooks'
import { exportDocumentsToExcelRequest } from '../../api/exportDocumentsToExcel'

const DocumentsTable = () => {
  const [documents, setDocuments] = useState([])
  const [lastSPVCrawls, setLastSPVCrawls] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState(
    () => new URLSearchParams(window.location.search).get('searchTerm') ?? ''
  )
  const [organisations, setOrganisations] = useState([])
  const [availableStatuses, setAvailableStatuses] = useState([])
  const [availableInvoiceGroups, setAvailableInvoiceGroups] = useState([])
  const [availableCategories, setAvailableCategories] = useState([])
  const [totalPages, setTotalPages] = useState(1)
  const [isHovered, setIsHovered] = useState(false)
  const [pointOfWorks, setPointOfWorks] = useState([])
  const toast = useToast()
  const user = useAuth().user
  const debouncedSearch = useDebounce(searchTerm, 300)

  function getInitialFilters() {
    const urlParams = new URLSearchParams(window.location.search)
    const statusFilterParam = urlParams.get('statusFilter')
    const invoiceGroupFilterParam = urlParams.get('invoiceGroupFilter')
    const categoryFilterParam = urlParams.get('categoryFilter')
    const companyFilterParam = urlParams.get('companyFilter')
    const pointOfWorkTagsParam = urlParams.get('pointOfWorkTags')
    const partnersParam = urlParams.get('partners')
    const initialIssueDateFilter = {
      from: urlParams.get('fromIssueDate')
        ? new Date(Number(urlParams.get('fromIssueDate')))
        : null,
      to: urlParams.get('toIssueDate')
        ? new Date(Number(urlParams.get('toIssueDate')))
        : null
    }

    const initialDueDateFilter = {
      from: urlParams.get('fromDueDate')
        ? new Date(Number(urlParams.get('fromDueDate')))
        : null,
      to: urlParams.get('toDueDate')
        ? new Date(Number(urlParams.get('toDueDate')))
        : null
    }

    let partners = []
    if (partnersParam) {
      const partnerCifs = partnersParam.split(',')
      const storedPartners = localStorage.getItem('partnerObjects')
      if (storedPartners) {
        try {
          const parsedPartners = JSON.parse(storedPartners)
          partners = parsedPartners.filter((partner) =>
            partnerCifs.includes(partner.cif)
          )
        } catch (error) {
          console.error(MESSAGE.LOCAL_STORAGE_ERROR.error, error)
        }
      }
    }

    let pointOfWorks = []
    if (pointOfWorkTagsParam) {
      const pointOfWorkCodes = pointOfWorkTagsParam.split(',')
      const storedPointOfWorks = localStorage.getItem('pointOfWorkObjects')
      if (storedPointOfWorks) {
        try {
          const parsedPointOfWork = JSON.parse(storedPointOfWorks)
          pointOfWorks = parsedPointOfWork.filter((pointOfWork) =>
            pointOfWorkCodes.includes(pointOfWork.code)
          )
        } catch (error) {
          console.error(MESSAGE.LOCAL_STORAGE_ERROR.error, error)
        }
      }
    }

    return {
      status: statusFilterParam ? statusFilterParam.split(',') : [],
      invoiceGroup: invoiceGroupFilterParam
        ? invoiceGroupFilterParam.split(',')
        : [],
      category: categoryFilterParam ? categoryFilterParam.split(',') : [],
      company: companyFilterParam ? companyFilterParam.split(',') : [],
      pointOfWorkTags: pointOfWorks,
      partners: partners,
      initialIssueDateFilter: initialIssueDateFilter,
      initialDueDateFilter: initialDueDateFilter
    }
  }

  const initialFilters = getInitialFilters()

  const [statusFilter, setStatusFilter] = useState(initialFilters.status)
  const [invoiceGroupFilter, setInvoiceGroupFilter] = useState(
    initialFilters.invoiceGroup
  )
  const [categoryFilter, setCategoryFilter] = useState(initialFilters.category)
  const [companyFilter, setCompanyFilter] = useState(initialFilters.company)
  const [pointOfWorkTags, setPointOfWorkTags] = useState(
    initialFilters.pointOfWorkTags
  )
  const [partners, setPartners] = useState(initialFilters.partners)
  const [issueDateFilter, setIssueDateFilter] = useState(
    initialFilters.initialIssueDateFilter
  )
  const [dueDateFilter, setDueDateFilter] = useState(
    initialFilters.initialDueDateFilter
  )

  const [pagination, setPagination] = useState(() => {
    const url = new URLSearchParams(window.location.search)
    const pageNumber = parseInt(url.get('page'), 10) || 0
    const size = parseInt(url.get('size'), 10) || 20
    let orderByList = [{ field: 'spvTimestamp', direction: 'DESCENDING' }]

    if (url.get('sort')) {
      const [field, direction] = url.get('sort').split(',')
      orderByList = [{ field, direction }]
    }

    return { pageNumber, size, orderByList }
  })

  const [pageInput, setPageInput] = useState(pagination.pageNumber + 1)

  useEffect(() => {
    setPageInput(pagination.pageNumber + 1)
  }, [pagination.pageNumber])

  const navigate = useNavigate()

  const getSortIcon = (field) => {
    const sortField = pagination.orderByList[0]?.field
    const sortDirection = pagination.orderByList[0]?.direction

    if (sortField === field) {
      return sortDirection === 'ASCENDING' ? <FaSortUp /> : <FaSortDown />
    }
    return <FaSort />
  }

  const handleSort = (field) => {
    setPagination((prev) => {
      const existingSort = prev.orderByList.find(
        (order) => order.field === field
      )
      let newDirection = 'ASCENDING'

      if (existingSort) {
        newDirection =
          existingSort.direction === 'ASCENDING' ? 'DESCENDING' : 'ASCENDING'
      }

      return {
        ...prev,
        orderByList: [{ field, direction: newDirection }]
      }
    })
  }

  const goToPage = () => {
    const parsedPage = parseInt(pageInput, 10)
    if (!isNaN(parsedPage) && parsedPage >= 1 && parsedPage <= totalPages) {
      setPagination((prev) => ({ ...prev, pageNumber: parsedPage - 1 }))
    } else {
      toast({
        title: 'Număr de pagină invalid',
        description: `Introduceți un număr între 1 și ${totalPages}.`,
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  const partnerCifs = useMemo(() => partners.map((p) => p.cif), [partners])

  const fetchDocuments = async () => {
    setLoading(true)

    try {
      const { pageNumber, size, orderByList } = pagination
      const selectedOrganisationCifs =
        companyFilter.length > 0
          ? organisations
              .filter((org) => companyFilter.includes(org.name))
              .map((org) => org.cif)
          : null

      const response = await getAllDocuments({
        searchTerm: searchTerm || null,
        organisationCifs: selectedOrganisationCifs,
        statuses: statusFilter.length > 0 ? statusFilter : null,
        invoiceGroups:
          invoiceGroupFilter.length > 0 ? invoiceGroupFilter : null,
        categories: categoryFilter.length > 0 ? categoryFilter : null,
        partnerCifs: partnerCifs,
        pointOfWorkTags: pointOfWorkTags.map((pointOfWork) => pointOfWork.code),
        syncStatuses: ['COMPLETED'],
        pageNumber,
        size,
        orderByList,
        fromIssueDate: issueDateFilter?.from
          ? issueDateFilter.from.getTime()
          : null,
        toIssueDate: issueDateFilter?.to ? issueDateFilter.to.getTime() : null,
        fromDueDate: dueDateFilter?.from ? dueDateFilter.from.getTime() : null,
        toDueDate: dueDateFilter?.to ? dueDateFilter.to.getTime() : null
      })

      if (response?.content) {
        setDocuments(response.content)
        const total = Math.ceil((response.total || 0) / size)
        setTotalPages(total)
      } else {
        setDocuments([])
        setTotalPages(1)
      }
    } catch (error) {
      toast({
        title: MESSAGE.ERROR.label,
        description: MESSAGE.SUPPORTING_DOCUMENTS_ERRORS.upload,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
      console.error(MESSAGE.SUPPORTING_DOCUMENTS_ERRORS.upload, error)
    } finally {
      setLoading(false)
    }
  }

  const getPageNumbers = (currentPage, totalPages, maxButtons = 5) => {
    const pages = []
    const half = Math.floor(maxButtons / 2)
    let startPage = Math.max(0, currentPage - half)
    let endPage = Math.min(totalPages - 1, currentPage + half)

    if (currentPage - half < 0) {
      endPage = Math.min(totalPages - 1, endPage + (half - currentPage))
    }
    if (currentPage + half > totalPages - 1) {
      startPage = Math.max(
        0,
        startPage - (currentPage + half - (totalPages - 1))
      )
    }

    if (startPage > 0) {
      pages.push(0)
      if (startPage > 1) {
        pages.push('ellipsis-left')
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) {
        pages.push('ellipsis-right')
      }
      pages.push(totalPages - 1)
    }

    return pages
  }

  const fetchLastSPVCrawls = async () => {
    try {
      const response = await getLastSPVCrawls()
      if (response) {
        setLastSPVCrawls(response || [])
      }
    } catch (error) {
      console.error(MESSAGE.SPV_ERROR.label, error)
    }
  }

  const clearAllFilters = () => {
    setSearchTerm('')
    setCompanyFilter([])
    setCategoryFilter([])
    setStatusFilter([])
    setInvoiceGroupFilter([])
    setPointOfWorkTags([])
    setPartners([])
    setIssueDateFilter({ from: null, to: null })
    setDueDateFilter({ from: null, to: null })
    setPagination((prev) => ({ ...prev, pageNumber: 0 }))
    updateUrlParams()
  }

  const exportDocuments = async () => {
    setLoading(true)
    try {
      const { pageNumber, size, orderByList } = pagination
      const selectedOrganisationCifs =
        companyFilter.length > 0
          ? organisations
              .filter((org) => companyFilter.includes(org.name))
              .map((org) => org.cif)
          : null
      await exportDocumentsToExcelRequest({
        searchTerm: searchTerm || null,
        organisationCifs: selectedOrganisationCifs,
        statuses: statusFilter.length > 0 ? statusFilter : null,
        invoiceGroups:
          invoiceGroupFilter.length > 0 ? invoiceGroupFilter : null,
        categories: categoryFilter.length > 0 ? categoryFilter : null,
        partnerCifs: partnerCifs,
        pointOfWorkTags: pointOfWorkTags.map((pointOfWork) => pointOfWork.code),
        syncStatuses: ['COMPLETED'],
        pageNumber,
        size,
        orderByList,
        fromIssueDate: issueDateFilter?.from
          ? issueDateFilter.from.getTime()
          : null,
        toIssueDate: issueDateFilter?.to ? issueDateFilter.to.getTime() : null,
        fromDueDate: dueDateFilter?.from ? dueDateFilter.from.getTime() : null,
        toDueDate: dueDateFilter?.to ? dueDateFilter.to.getTime() : null
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    localStorage.setItem('partnerObjects', JSON.stringify(partners))
  }, [partners])

  useEffect(() => {
    localStorage.setItem('pointOfWorkObjects', JSON.stringify(pointOfWorkTags))
  }, [pointOfWorkTags])

  useEffect(() => {
    refreshNomenclatures()
  }, [
    statusFilter,
    invoiceGroupFilter,
    companyFilter,
    categoryFilter,
    partners,
    pointOfWorkTags
  ])

  useEffect(() => {
    updateUrlParams()
    fetchDocuments()
  }, [
    pagination,
    debouncedSearch,
    statusFilter,
    invoiceGroupFilter,
    companyFilter,
    categoryFilter,
    partners,
    pointOfWorkTags,
    issueDateFilter,
    dueDateFilter
  ])

  async function refreshNomenclatures() {
    const selectedFilters = {
      categories: categoryFilter,
      pointOfWorks: pointOfWorkTags.map((pointOfWork) => pointOfWork.code),
      partners: partnerCifs,
      statuses: statusFilter,
      invoiceGroups: invoiceGroupFilter,
      companies:
        companyFilter.length > 0
          ? organisations
              .filter((org) => companyFilter.includes(org.name))
              .map((org) => org.cif)
          : null
    }

    try {
      const [rawInvoiceGroups, rawStatuses, rawCats, rawOrgs] =
        await Promise.all([
          getInvoiceGroups(selectedFilters),
          getStatuses(selectedFilters),
          getCategories(selectedFilters),
          getOrganisations(selectedFilters)
        ])

      setAvailableInvoiceGroups(['N/A', ...rawInvoiceGroups.map((item) => item.name)])

      const allStatuses = rawStatuses.map((i) => i.status)
      setAvailableStatuses(
        allStatuses.filter((s) =>
          [
            DocumentStatus.PENDING.key,
            DocumentStatus.PROCESSED.key,
            DocumentStatus.APPROVED.key,
            DocumentStatus.SENT_TO_OMC.key
          ].includes(s)
        )
      )

      setAvailableCategories(rawCats.map((item) => item.category) || [])
      setOrganisations(rawOrgs || [])
    } catch (err) {
      console.error('Error loading nomenclatures:', err)
      toast({
        title: MESSAGE.ERROR.label,
        description: MESSAGE.ORGANISATION_ERROR.label,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  useEffect(() => {
    getPointOfWorks({ cif: null })
      .then((response) => {
        const dictionar = response.content.reduce((acc, item) => {
          acc[item.code] = item.name
          return acc
        }, {})
        setPointOfWorks(dictionar)
      })
      .catch((error) => {
        console.error(MESSAGE.POINT_OF_WORKS.error, error)
      })
  }, [])

  useEffect(() => {
    fetchLastSPVCrawls()
  }, [])

  const updateUrlParams = () => {
    return new Promise((resolve) => {
      const urlParams = new URLSearchParams(window.location.search)

      if (pagination.orderByList.length > 0) {
        const { field, direction } = pagination.orderByList[0]
        urlParams.set('sort', `${field},${direction}`)
      }

      urlParams.set('page', pagination.pageNumber)
      urlParams.set('size', pagination.size)

      if (statusFilter.length > 0) {
        urlParams.set('statusFilter', statusFilter.join(','))
      } else {
        urlParams.delete('statusFilter')
      }

      if (invoiceGroupFilter.length > 0) {
        urlParams.set('invoiceGroupFilter', invoiceGroupFilter.join(','))
      } else {
        urlParams.delete('invoiceGroupFilter')
      }

      if (categoryFilter.length > 0) {
        urlParams.set('categoryFilter', categoryFilter.join(','))
      } else {
        urlParams.delete('categoryFilter')
      }

      if (companyFilter.length > 0) {
        urlParams.set('companyFilter', companyFilter.join(','))
      } else {
        urlParams.delete('companyFilter')
      }

      if (searchTerm) {
        urlParams.set('searchTerm', searchTerm)
      } else {
        urlParams.delete('searchTerm')
      }

      if (pointOfWorkTags.length > 0) {
        urlParams.set(
          'pointOfWorkTags',
          pointOfWorkTags.map((p) => p.code).join(',')
        )
      } else {
        urlParams.delete('pointOfWorkTags')
      }

      if (partners.length > 0) {
        urlParams.set('partners', partners.map((p) => p.cif).join(','))
      } else {
        urlParams.delete('partners')
      }

      if (issueDateFilter && issueDateFilter.from) {
        urlParams.set('fromIssueDate', issueDateFilter.from.getTime())
      } else {
        urlParams.delete('fromIssueDate')
      }
      if (issueDateFilter && issueDateFilter.to) {
        urlParams.set('toIssueDate', issueDateFilter.to.getTime())
      } else {
        urlParams.delete('toIssueDate')
      }

      if (dueDateFilter && dueDateFilter.from) {
        urlParams.set('fromDueDate', dueDateFilter.from.getTime())
      } else {
        urlParams.delete('fromDueDate')
      }
      if (dueDateFilter && dueDateFilter.to) {
        urlParams.set('toDueDate', dueDateFilter.to.getTime())
      } else {
        urlParams.delete('toDueDate')
      }

      window.history.pushState({}, '', `?${urlParams.toString()}`)
      resolve()
    })
  }

  const renderTable = () => {
    if (loading) {
      return (
        <Box textAlign="center" p="4">
          <Spinner size="lg" />
        </Box>
      )
    }

    return (
      <Table
        variant="simple"
        size="sm"
        sx={{ fontSize: 'sm', th: { p: 0.5 }, td: { p: 0.5 } }}
      >
        <Thead>
          <Tr>
            <Th>#</Th>
            <Th>
              <FilterMultiSelectPopover
                label="Org."
                options={organisations.map((o) => ({
                  value: o.name,
                  label: o.name
                }))}
                selectedValues={companyFilter}
                onChange={(newVals) => {
                  setCompanyFilter(newVals)
                  setPagination((prev) => ({ ...prev, pageNumber: 0 }))
                }}
              />
            </Th>
            <Th cursor="pointer">
              <Flex align="center">
                {LABEL.RULES_TABLE.pointOfWork}
                {pointOfWorkTags && pointOfWorkTags.length > 0 && (
                  <Text ml={1} fontSize="sm" color="blue.500">
                    ({pointOfWorkTags.length})
                  </Text>
                )}
                <FilterPopoverWithCheck
                  label={LABEL.FILTER.label}
                  initialSelected={pointOfWorkTags}
                  onSubmit={(vals) => {
                    setPointOfWorkTags(vals);
                    setPagination(prev =>
                      prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }
                    );
                  }}
                  fetchItems={getDecoratedPointOfWorks}
                  pageSize={10}
                  content={true}
                  cif={user.company}
                  isScrollable={true}
                  extraFilters={{
                    categories: categoryFilter,
                    pointOfWorks: pointOfWorkTags.map(
                      (pointOfWork) => pointOfWork.code
                    ),
                    partners: partnerCifs,
                    statuses: statusFilter,
                    invoiceGroups: invoiceGroupFilter,
                    companies:
                      companyFilter.length > 0
                        ? organisations
                            .filter((org) => companyFilter.includes(org.name))
                            .map((org) => org.cif)
                        : null
                  }}
                />
              </Flex>
            </Th>
            <Th>
              <FilterMultiSelectPopover
                label="Categorie"
                options={availableCategories.map((c) => ({
                  value: c,
                  label: c
                }))}
                selectedValues={categoryFilter}
                onChange={(newVals) => {
                  setCategoryFilter(newVals)
                  setPagination((prev) => ({ ...prev, pageNumber: 0 }))
                }}
              />
            </Th>
            <Th cursor="pointer">
              <Flex align="center">
                {LABEL.RULES_TABLE.partnerName}
                {partners && partners.length > 0 && (
                  <Text ml={1} fontSize="sm" color="blue.500">
                    ({partners.length})
                  </Text>
                )}
                <span
                  onClick={() => handleSort('partner.name')}
                  style={{ marginRight: '8px' }}
                >
                  <Icon as={() => getSortIcon('partner.name')} ml="2" />
                </span>
                <FilterPopoverWithCheck
                  label={LABEL.FILTER.label}
                  initialSelected={partners}
                  onSubmit={(vals) => {
                    setPartners(vals);
                    setPagination(prev =>
                      prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }
                    );
                  }}
                  fetchItems={getPartners}
                  displayField="name"
                  pageSize={10}
                  cif={user.company}
                  content={true}
                  extraFilters={{
                    categories: categoryFilter,
                    pointOfWorks: pointOfWorkTags.map(
                      (pointOfWork) => pointOfWork.code
                    ),
                    partners: partnerCifs,
                    statuses: statusFilter,
                    invoiceGroups: invoiceGroupFilter,
                    companies:
                      companyFilter.length > 0
                        ? organisations
                            .filter((org) => companyFilter.includes(org.name))
                            .map((org) => org.cif)
                        : null
                  }}
                />
              </Flex>
            </Th>
            <Th cursor="pointer" onClick={() => handleSort('series')}>
              <Flex align="center">
                {LABEL.PROCESS_TABLE_HEAD.documentNumber}{' '}
                <Icon as={() => getSortIcon('series')} ml="2" />
              </Flex>
            </Th>

            <Th>
              <FilterMultiSelectPopover
                label={LABEL.PROCESS_TABLE_HEAD.invoiceGroup}
                options={availableInvoiceGroups.map((s) => ({
                  value: s,
                  label: getPrettyStatus(s)
                }))}
                selectedValues={invoiceGroupFilter}
                onChange={(newVals) => {
                  setInvoiceGroupFilter(newVals)
                  setPagination((prev) => ({ ...prev, pageNumber: 0 }))
                }}
              />
            </Th>

            <Th
              cursor="pointer"
              onClick={() => handleSort('documentTotal.totalValue')}
            >
              <Flex align="center">
                {LABEL.PROCESS_TABLE_HEAD.totalValue}
                <Icon
                  as={() => getSortIcon('documentTotal.totalValue')}
                  ml="2"
                />
              </Flex>
            </Th>
            <Th cursor="pointer" onClick={() => handleSort('spvTimestamp')}>
              <Flex align="center">
                {LABEL.PROCESS_TABLE_HEAD.uploadDate}
                <Icon as={() => getSortIcon('spvTimestamp')} ml="2" />
              </Flex>
            </Th>
            <Th cursor="pointer">{LABEL.PROCESS_TABLE_HEAD.uploadIndex}</Th>
            <Th cursor="pointer" onClick={() => handleSort('issueDate')}>
              <Flex align="center">
                {LABEL.PROCESS_TABLE_HEAD.issueDate}{' '}
                <Icon as={() => getSortIcon('issueDate')} ml="2" />
              </Flex>
            </Th>
            <Th cursor="pointer" onClick={() => handleSort('dueDate')}>
              <Flex align="center">
                {LABEL.PROCESS_TABLE_HEAD.dueDate}{' '}
                <Icon as={() => getSortIcon('dueDate')} ml="2" />
              </Flex>
            </Th>

            <Th>
              <FilterMultiSelectPopover
                label="Status"
                options={availableStatuses.map((s) => ({
                  value: s,
                  label: getPrettyStatus(s)
                }))}
                selectedValues={statusFilter}
                onChange={(newVals) => {
                  setStatusFilter(newVals)
                  setPagination((prev) => ({ ...prev, pageNumber: 0 }))
                }}
              />
            </Th>

            <Th>{LABEL.ACTIONS.label}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {documents.length > 0 ? (
            documents.map((doc, index) => (
              <Tr
                key={doc.id}
                onClick={() => {
                  const urlParams = new URLSearchParams()
                  urlParams.set('page', pagination.pageNumber)
                  urlParams.set('size', pagination.size)
                  if (pagination.orderByList.length > 0) {
                    const { field, direction } = pagination.orderByList[0]
                    urlParams.set('sort', `${field},${direction}`)
                  }
                  if (searchTerm) {
                    urlParams.set('searchTerm', searchTerm)
                  }
                  if (statusFilter.length > 0) {
                    urlParams.set('statusFilter', statusFilter.join(','))
                  }
                  if (invoiceGroupFilter.length > 0) {
                    urlParams.set(
                      'invoiceGroupFilter',
                      invoiceGroupFilter.join(',')
                    )
                  }
                  if (categoryFilter.length > 0) {
                    urlParams.set('categoryFilter', categoryFilter.join(','))
                  }
                  if (companyFilter.length > 0) {
                    urlParams.set('companyFilter', companyFilter.join(','))
                  }
                  navigate(`/documente/${doc.id}?${urlParams.toString()}`)
                }}
                cursor="pointer"
                _hover={{ backgroundColor: 'gray.100' }}
              >
                <Td borderRadius="md" border="1px solid" borderColor="gray.200">
                  {pagination.pageNumber * pagination.size + index + 1}
                </Td>
                <Td>
                  <Text altText={doc.organisation.name}>
                    {getWCRShortName(doc.organisation?.cif) ||
                      doc.organisation.name}
                  </Text>
                </Td>
                <Td>{doc.organisation.pointOfWorkTag || "N/A"}</Td>
                <Td>{doc.category || 'N/A'}</Td>
                <Td>{doc.partner?.name || 'N/A'}</Td>
                <Td>{doc.series}</Td>
                <Td>{doc.invoiceGroup || 'N/A'}</Td>
                <Td style={{ textAlign: "right", paddingRight: "10px"  }}>
                    {doc.documentTotal?.totalValue.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Td>
                <Td>
                  {doc.spvTimestamp
                    ? format(new Date(doc.spvTimestamp), 'yyyy-MM-dd HH:mm')
                    : '-'}
                </Td>
                <Td>{doc.downloadId}</Td>

                <Td>
                  {doc.issueDate
                    ? format(new Date(doc.issueDate), 'yyyy-MM-dd')
                    : '-'}
                </Td>
                <Td>
                  {doc.dueDate
                    ? format(new Date(doc.dueDate), 'yyyy-MM-dd')
                    : '-'}
                </Td>

                <Td>
                  <Badge
                    colorScheme={
                      doc.status === DOCUMENT_STATUS.PENDING.key
                        ? 'gray'
                        : doc.status === DOCUMENT_STATUS.PROCESSED.key
                        ? 'blue'
                        : doc.status === DOCUMENT_STATUS.APPROVED.key
                        ? 'orange'
                        : doc.status === DOCUMENT_STATUS.SENT_TO_OMC.key
                        ? 'green'
                        : 'gray'
                    }
                    px="3"
                    py="1"
                    borderRadius="full"
                    fontSize=".65em"
                  >
                    {getPrettyStatus(doc.status)}
                  </Badge>
                </Td>

                <Td>
                  <Flex gap="2">
                    <Tooltip label="Export PDF" hasArrow>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        _hover={{ bg: 'blue.500', color: 'white' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          exportPdfRequest(doc.id)
                        }}
                      >
                        <Icon as={FaFilePdf} />
                      </Button>
                    </Tooltip>
                    <Tooltip label="Export XML" hasArrow>
                      <Button
                        size="sm"
                        colorScheme="green"
                        variant="outline"
                        _hover={{ bg: 'green.500', color: 'white' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          exportXmlRequest(doc.id)
                        }}
                      >
                        <Icon as={FaFile} />
                      </Button>
                    </Tooltip>
                    <Tooltip label="Export ZIP" hasArrow>
                      <Button
                        size="sm"
                        colorScheme="orange"
                        variant="outline"
                        _hover={{ bg: 'orange.500', color: 'white' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          exportDocumentZIPRequest(doc.id)
                        }}
                      >
                        <Icon as={FaFolder} />
                      </Button>
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan="13" textAlign="center">
                Nu s-au găsit documente.
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    )
  }

  return (
    <>
      <Flex align="center" justifyContent="space-between" width="100%" mb="5">
        <Box
          width="15%"
          px={4}
          py={2}
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
          color="gray.700"
          bg="gray.50"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          position="relative"
          mr={4}
        >
          <Text
            fontSize="sm"
            fontWeight="bold"
            gap={2}
            align="center"
            justify="center"
          >
            <span style={{ marginRight: '8px' }}>
              <i className="far fa-clock" />
            </span>
            {LABEL.SPV_SYNC.lastSync}
          </Text>

          {isHovered && (
            <Box
              position="absolute"
              top="100%"
              left="0"
              mt={2}
              p={4}
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
              color="gray.700"
              bg="gray.50"
              zIndex="1"
              width="auto"
              minWidth="300px"
              maxWidth="none"
              whiteSpace="nowrap"
              overflow="visible"
              boxSizing="border-box"
            >
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>{LABEL.SPV_SYNC.organisation}</Th>
                    <Th>{LABEL.SPV_SYNC.lastUpdate}</Th>
                    <Th>{LABEL.SPV_SYNC.indexedDocuments}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {lastSPVCrawls.map((data) => (
                    <Tr key={data.cif}>
                      <Td>
                        {organisations.find((org) => org.cif === data.cif)
                          ?.name || 'N/A'}
                      </Td>
                      <Td>
                        {data.startTimestamp
                          ? format(
                              new Date(data.startTimestamp),
                              'yyyy-MM-dd HH:mm'
                            )
                          : '-'}
                      </Td>
                      <Td>{data.totalEntries || '-'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
        <Flex align="center" gap={4} width="85%">
          <InputGroup flex="1">
            <InputLeftElement color="gray.500" pointerEvents="none">
              <FaSearch />
            </InputLeftElement>
            <Input
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPagination(prev => prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 });
              }}
              placeholder={LABEL.SEARCH_TERM.placeholder}
            />
          </InputGroup>

          <Box minW="200px">
            <DateRangePicker
              selected={issueDateFilter}
              onApply={(range) => {
                setPagination((prev) => ({ ...prev, pageNumber: 0 }))
                setIssueDateFilter(range)
              }}
              dateFormat="dd.MM.yyyy"
              placeholder="Emitere"
            />
          </Box>

          <Box minW="200px">
            <DateRangePicker
              selected={dueDateFilter}
              onApply={(range) => {
                setPagination((prev) => ({ ...prev, pageNumber: 0 }))
                setDueDateFilter(range)
              }}
              dateFormat="dd.MM.yyyy"
              placeholder="Scadență"
            />
          </Box>
          <Button
            size="sm"
            colorScheme="red"
            onClick={clearAllFilters}
            leftIcon={<FaTrash />}
          >
            {LABEL.PROCESS_TABLE_HEAD.deleteFilters}
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={exportDocuments}
            leftIcon={<FaFileExcel />}
          >
            {LABEL.PROCESS_TABLE_HEAD.exportDocuments}
          </Button>
        </Flex>
      </Flex>

      {renderTable()}

      <Flex justify="space-between" mt="4" align="center">
        <Flex>
          <Select
            value={pagination.size}
            onChange={(e) =>
              setPagination((prev) => ({
                ...prev,
                size: Number(e.target.value),
                pageNumber: 0
              }))
            }
            width="fit-content"
          >
            {Array.from({ length: 20 }, (_, i) => (i + 1) * 5).map((size) => (
              <option key={size} value={size}>
                {LABEL.DOCUMENTS_PER_PAGE.label(size)}
              </option>
            ))}
          </Select>
        </Flex>

        <Flex direction="column" align="center">
          <Text mb={2}>
            Pagina {pagination.pageNumber + 1} / {totalPages}
          </Text>
          <Flex align="center" gap="2">
            <Button
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  pageNumber: Math.max(prev.pageNumber - 1, 0)
                }))
              }
              isDisabled={pagination.pageNumber === 0}
              colorScheme="blue"
              variant="outline"
              size="sm"
              leftIcon={<FaChevronLeft />}
            >
              Precedent
            </Button>

            {getPageNumbers(pagination.pageNumber, totalPages).map((item) =>
              typeof item === 'number' ? (
                <Button
                  key={`page-${item}`}
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, pageNumber: item }))
                  }
                  variant={pagination.pageNumber === item ? 'solid' : 'outline'}
                  size="sm"
                >
                  {item + 1}
                </Button>
              ) : (
                <Text key={item} px="2">
                  …
                </Text>
              )
            )}

            <Button
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  pageNumber: Math.min(prev.pageNumber + 1, totalPages - 1)
                }))
              }
              isDisabled={pagination.pageNumber >= totalPages - 1}
              colorScheme="blue"
              variant="outline"
              size="sm"
              rightIcon={<Icon as={FaChevronRight} />}
            >
              Următor
            </Button>
          </Flex>

          <Flex mt="2" align="center" gap="2">
            <Text>Accesează pagina:</Text>
            <Input
              type="number"
              value={pageInput}
              onFocus={() => setPageInput('')}
              onChange={(e) => setPageInput(e.target.value)}
              onBlur={() => {
                if (pageInput === '') {
                  setPageInput(String(pagination.pageNumber + 1))
                }
              }}
              width="60px"
              min="1"
              max={totalPages}
              size="sm"
            />
            <Button size="sm" onClick={goToPage}>
              Aplică
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}
export default DocumentsTable
